import { useCallback, useEffect, useState } from 'react';
import { Plus, X } from 'lucide-react';
import type { AddItem } from '../../types/actions';
import type { FactionRelationship, CharacterClass, Organization } from '../../../shared/types';
import TextAreaField from '../ui/TextAreaField';

export default function PlayerCharacterForm({
  addItem,
  submitRef,
  showSubmit = true,
  organizations = [],
}: {
  addItem: AddItem;
  submitRef?: React.MutableRefObject<(() => void) | null>;
  showSubmit?: boolean;
  organizations?: Organization[];
}) {
  const [pc, setPc] = useState({
    characterName: '',
    playerName: '',
    class: '',
    level: 1,
    faction: '',
    personalHeat: 0,
    heatNotes: '',
    factionRelationships: [] as FactionRelationship[],
    notes: '',
    classes: [{ name: '', level: 1 }] as CharacterClass[],
  });
  const [errors, setErrors] = useState<{ characterName?: string }>({});

  const handleSubmit = useCallback(() => {
    if (!pc.characterName.trim()) {
      setErrors({ characterName: 'Character name is required.' });
      return;
    }

    const initialRelationships = pc.faction
      ? [
          {
            id: Date.now(),
            faction: pc.faction,
            renown: 1,
          },
        ]
      : [];

    // Derive class/level from classes array
    const validClasses = pc.classes.filter(c => c.name.trim());
    const derivedClass = validClasses.length > 0
      ? validClasses.map(c => c.name).join(' / ')
      : pc.class;
    const derivedLevel = validClasses.length > 0
      ? validClasses.reduce((sum, c) => sum + (c.level || 1), 0)
      : pc.level;

    addItem('playerCharacters', {
      ...pc,
      class: derivedClass,
      level: derivedLevel,
      classes: validClasses.length > 0 ? validClasses : null,
      factionRelationships: initialRelationships,
    });
    setPc({
      characterName: '',
      playerName: '',
      class: '',
      level: 1,
      faction: '',
      personalHeat: 0,
      heatNotes: '',
      factionRelationships: [],
      notes: '',
      classes: [{ name: '', level: 1 }],
    });
    setErrors({});
  }, [addItem, pc]);

  useEffect(() => {
    if (submitRef) {
      submitRef.current = handleSubmit;
    }
    return () => {
      if (submitRef) {
        submitRef.current = null;
      }
    };
  }, [submitRef, handleSubmit]);

  return (
    <div className="bg-[var(--accent-muted)] p-4 rounded-lg border border-[var(--glass-border)] mb-4">
      <div className="field-block">
        <label className="field-label">Character Name</label>
        <input
          type="text"
          placeholder="Character Name"
          value={pc.characterName}
          onChange={(e) => {
            setPc({ ...pc, characterName: e.target.value });
            if (errors.characterName) setErrors({});
          }}
          className={`input-field ${errors.characterName ? 'input-error' : ''}`}
        />
        <div className="field-meta">
          <span className={errors.characterName ? 'field-error' : 'field-hint'}>
            {errors.characterName || 'Required.'}
          </span>
        </div>
      </div>
      <div className="field-block">
        <label className="field-label">Player Name</label>
        <input
          type="text"
          placeholder="Player Name"
          value={pc.playerName}
          onChange={(e) => setPc({ ...pc, playerName: e.target.value })}
          className="input-field"
        />
      </div>
      <div className="field-block">
        <label className="field-label">Classes</label>
        {pc.classes.map((cls, idx) => (
          <div key={idx} className="flex gap-2 mb-2 items-end">
            <div className="flex-1">
              {idx === 0 && <span className="text-xs text-[var(--text-muted)]">Class</span>}
              <input
                type="text"
                placeholder="Class name"
                value={cls.name}
                onChange={(e) => {
                  const updated = [...pc.classes];
                  updated[idx] = { ...updated[idx], name: e.target.value };
                  setPc({ ...pc, classes: updated });
                }}
                className="input-field"
              />
            </div>
            <div className="w-20">
              {idx === 0 && <span className="text-xs text-[var(--text-muted)]">Level</span>}
              <input
                type="number"
                min="1"
                max="20"
                value={cls.level}
                onChange={(e) => {
                  const updated = [...pc.classes];
                  updated[idx] = { ...updated[idx], level: parseInt(e.target.value) || 1 };
                  setPc({ ...pc, classes: updated });
                }}
                className="input-field"
              />
            </div>
            {pc.classes.length > 1 && (
              <button
                type="button"
                onClick={() => {
                  const updated = pc.classes.filter((_, i) => i !== idx);
                  setPc({ ...pc, classes: updated });
                }}
                className="icon-button danger mb-0.5"
                aria-label="Remove class"
                title="Remove class"
              >
                <X size={14} />
              </button>
            )}
          </div>
        ))}
        <button
          type="button"
          onClick={() => setPc({ ...pc, classes: [...pc.classes, { name: '', level: 1 }] })}
          className="text-button text-xs flex items-center gap-1"
        >
          <Plus size={12} /> Add Class
        </button>
      </div>
      <div className="field-block">
        <label className="field-label">Faction</label>
        <select
          value={pc.faction}
          onChange={(e) => setPc({ ...pc, faction: e.target.value })}
          className="input-field"
        >
          <option value="">Select Faction</option>
          {organizations.map((org) => (
            <option key={org.id} value={org.name}>{org.name}</option>
          ))}
        </select>
      </div>
      <TextAreaField
        label="Notes"
        placeholder="Notes"
        value={pc.notes}
        onChange={(value) => setPc({ ...pc, notes: value })}
        hint="Personality, goals, and hooks."
        showCount
        minRows={4}
      />
      {showSubmit && (
        <button
          onClick={handleSubmit}
          className="btn-primary-solid flex items-center gap-2"
        >
          <Plus size={16} /> Add Player Character
        </button>
      )}
    </div>
  );
}
